import { Building2, KeyRound, Sparkles, User, Users } from 'lucide-react';
import type { ModelProviderSecret, ModelProviderSecretScope } from '@reflex/shared';
import { MODEL_PROVIDER_LABELS } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { modelProviderSecretDisplayName } from './secret-display';

/**
 * Background/foreground treatment for the leading icon square, keyed by
 * the secret's tier. Mirrors `OwnerChip` so a user/team/org key reads
 * the same color language as the owner indicators elsewhere.
 */
const SCOPE_ICON_CLASSES: Record<ModelProviderSecretScope, string> = {
  user: 'bg-primary/10 text-primary',
  team: 'bg-secondary/40 text-foreground',
  org: 'bg-muted/40 text-muted-foreground',
};

/**
 * Owner glyph keyed by tier — the leading icon doubles as a scope cue so
 * two keys that share a name (e.g. a Team and an Org both called
 * "default") read as distinct at a glance, not just by the subtler
 * background tint. Mirrors `OwnerChip`'s user/team/organization icons.
 */
const SCOPE_OWNER_ICON: Record<ModelProviderSecretScope, typeof KeyRound> = {
  user: User,
  team: Users,
  org: Building2,
};

const TYPE_BADGE_LABEL: Record<ModelProviderSecret['type'], string> = {
  apiKey: 'Key',
  subscription: 'Sub',
};

/**
 * Subscription badges read with the same success-toned outline used on
 * the costs page so subscriptions look visually distinct from metered
 * API keys everywhere a key chip is rendered, not only in the costs UI.
 */
const TYPE_BADGE_CLASS: Record<ModelProviderSecret['type'], string> = {
  apiKey: '',
  subscription: 'border-status-success/40 text-status-success',
};

const TYPE_BADGE_VARIANT: Record<ModelProviderSecret['type'], 'secondary' | 'outline'> = {
  apiKey: 'secondary',
  subscription: 'outline',
};

/**
 * Glyph that, on its own, tells a subscription apart from a metered API
 * key — `Sparkles` for subscriptions (matching the picker's "Add
 * subscription" affordance and the costs page's success tone), a key for
 * everything else. Used by the compact, icon-only chip where there's no
 * room for the type badge.
 */
const TYPE_ICON: Record<ModelProviderSecret['type'], typeof KeyRound> = {
  apiKey: KeyRound,
  subscription: Sparkles,
};

/** Long-form type word for tooltips/aria where the badge isn't shown. */
const TYPE_WORD: Record<ModelProviderSecret['type'], string> = {
  apiKey: 'API key',
  subscription: 'subscription',
};

/**
 * Square tint for the compact icon, keyed by type so the subscription vs
 * API-key distinction survives even without the badge: subscriptions take
 * the success tone, API keys keep their owner-scope color.
 */
function compactSquareClass(secret: ModelProviderSecret): string {
  return secret.type === 'subscription'
    ? 'bg-status-success/15 text-status-success'
    : SCOPE_ICON_CLASSES[secret.scope];
}

export interface ModelProviderSecretChipProps {
  /** The selected secret, or `null` to render the empty placeholder. */
  secret: ModelProviderSecret | null;
  /** Label shown when `secret` is `null`. Defaults to "Choose key". */
  placeholder?: string;
  /**
   * Render just the type-distinct icon square (no provider/name text or
   * type badge) for tight bars where the full key string is too long. The
   * icon alone signals subscription vs API key; the surrounding control is
   * expected to expose the full details (e.g. in a popover).
   */
  compact?: boolean;
  className?: string;
}

/**
 * Compact, presentational summary of a model-provider secret selection.
 * Used as the trigger contents for {@link ModelProviderSecretPicker} and
 * anywhere a chosen key needs to be displayed inline. Pure render — no
 * data fetching or interactivity of its own.
 */
export function ModelProviderSecretChip({
  secret,
  placeholder = 'Choose key',
  compact = false,
  className,
}: ModelProviderSecretChipProps) {
  if (compact) {
    if (!secret) {
      return (
        <span
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted/40 text-muted-foreground',
            className,
          )}
          aria-label={placeholder}
          title={placeholder}
        >
          <KeyRound className="h-3 w-3" />
        </span>
      );
    }
    const CompactIcon = TYPE_ICON[secret.type];
    const label = `${MODEL_PROVIDER_LABELS[secret.provider]} ${TYPE_WORD[secret.type]}: ${modelProviderSecretDisplayName(secret)}`;
    return (
      <span
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded',
          compactSquareClass(secret),
          className,
        )}
        aria-label={label}
        title={label}
      >
        <CompactIcon className="h-3 w-3" />
      </span>
    );
  }

  if (!secret) {
    return (
      <span className={cn('flex min-w-0 items-center gap-1.5', className)}>
        <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-muted-foreground">{placeholder}</span>
      </span>
    );
  }

  const ScopeIcon = SCOPE_OWNER_ICON[secret.scope];
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <span
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded',
          SCOPE_ICON_CLASSES[secret.scope],
        )}
      >
        <ScopeIcon className="h-2.5 w-2.5" />
      </span>
      <span className="truncate">
        <span className="font-medium">{MODEL_PROVIDER_LABELS[secret.provider]}</span>
        <span className="text-muted-foreground"> · {modelProviderSecretDisplayName(secret)}</span>
      </span>
      <Badge
        variant={TYPE_BADGE_VARIANT[secret.type]}
        className={cn('shrink-0 px-1 py-0 text-2xs', TYPE_BADGE_CLASS[secret.type])}
      >
        {TYPE_BADGE_LABEL[secret.type]}
      </Badge>
    </span>
  );
}
